import { cx } from 'linaria';
import * as React from 'react';
import { useEffect } from 'react';
import { FormControl, Input, InputSizes, states, usePositioner } from '..';
import { IconAlertSmall, IconChevronDownSmall, IconChevronUpSmall, IconRemoveSmall } from '../foundation/icons';
import { SelectOptions } from '../select-options';
import { arraysEqual } from '../utils/array-util';
import * as styles from './select.styles';

export interface SelectOption {
    value: string;
    id: number | string;
    isSelected?: boolean;
    startIcon?: React.ReactElement;
    endIcon?: React.ReactElement;
    onClick?: () => void;
    isDisabled?: boolean;
}

export interface GroupedSelectOptions {
    groupName: string;
    isSelected?: boolean;
    startIcon?: React.ReactElement;
    menuItems: Array<SelectOption>;
}

export interface SelectProps {
    menuItems?: Array<SelectOption>;
    groupedMenuItems?: Array<GroupedSelectOptions>;
    maxDisplayValues?: number;
    label?: string;
    defaultOption?: SelectOption;
    placeholder?: string;
    annotation?: string;
    error?: string;
    createPlaceholderMessage?: string;
    searchPlaceholderMessage?: string;
    maxSelectedMessage?: string;
    isMultiselect?: boolean;
    isSearchable?: boolean;
    useOuterSearch?: boolean;
    isCreatable?: boolean;
    size?: InputSizes;
    inputValueFormat?: () => string;
    onCreateItem?: (item: SelectOption) => void;
    onSelectItem?: (items: Array<SelectOption>) => void;
    onOpenClose?: (inOpen: boolean) => void;
    style?: React.CSSProperties;
    testId?: string;
    disabled?: boolean;
    setVisible?: boolean;
    isNestedGroups?: boolean;
    autoComplete?: 'on' | 'off';
    isBorderless?: boolean;
    isInteractive?: boolean;
    isExternalUpdated?: boolean;
    deselectAllOnClear?: boolean;
    isClearButtonVisible?: boolean;
    resetGroupOnClear?: boolean;
    resetGroupOnSelect?: boolean;
    remainOpenOnClear?: boolean;
    hideSelectedCheckmark?: boolean;
    withDebounce?: boolean;
}

const CalculateOptions = (
    menuItems?: SelectOption[],
    groupedMenuItems?: GroupedSelectOptions[],
    isSelected?: boolean,
) => {
    let options: SelectOption[] = [];

    if (groupedMenuItems && groupedMenuItems.length > 0) {
        options = isSelected
            ? groupedMenuItems.map((group) => group.menuItems.filter((mi) => mi.isSelected && !mi.isDisabled)).flat()
            : groupedMenuItems.map((group) => group.menuItems).flat();
    } else if (menuItems && menuItems.length > 0) {
        options = isSelected ? menuItems.filter((item) => item.isSelected && !item.isDisabled) : menuItems;
    }

    return options;
};

export const Select = React.forwardRef<HTMLDivElement, SelectProps>(
    (props: React.PropsWithChildren<SelectProps>, ref) => {
        const {
            menuItems,
            groupedMenuItems,
            maxDisplayValues,
            isMultiselect,
            isSearchable,
            isCreatable,
            onCreateItem,
            onSelectItem,
            maxSelectedMessage,
            placeholder,
            useOuterSearch,
            error,
            testId = 'select',
            disabled,
            defaultOption,
            size,
            inputValueFormat,
            setVisible,
            onOpenClose,
            isNestedGroups,
            autoComplete = 'off',
            isBorderless,
            isInteractive,
            style,
            isExternalUpdated,
            deselectAllOnClear = true,
            isClearButtonVisible = isMultiselect,
            resetGroupOnClear,
            resetGroupOnSelect,
            remainOpenOnClear,
            hideSelectedCheckmark,
            withDebounce = false,
            ...passThroughProps
        } = props;

        const generateSelectedOptions = React.useCallback((): { id: string; value: string }[] => {
            const calculated = CalculateOptions(menuItems, groupedMenuItems, true).map((item) => ({
                id: String(item.id),
                value: item.value,
            }));
            if (defaultOption) {
                return [
                    { id: String(defaultOption.id), value: defaultOption.value },
                    ...calculated.filter((item) => item.id !== defaultOption.id),
                ];
            }

            return calculated;
        }, [defaultOption, groupedMenuItems, menuItems]);
        const [selectedOptions, setSelectedOptions] = React.useState<{ id: string; value: string }[]>(() =>
            generateSelectedOptions(),
        );

        const selectedOptionsIds = React.useMemo(() => selectedOptions.map((option) => option.id), [selectedOptions]);

        // Local state
        const [isDirty, setIsDirty] = React.useState(false);
        const inputRef = React.useRef<HTMLElement>(null);
        const [hasMounted, setHasMounted] = React.useState(false);
        const [isVisible, setIsVisible] = React.useState(false);
        const [value, setValue] = React.useState(defaultOption ? defaultOption.value : '');
        const [options, setOptions] = React.useState<SelectOption[]>(CalculateOptions(menuItems));
        const [groupOptions, setGroupOptions] = React.useState<GroupedSelectOptions[]>(groupedMenuItems || []);
        const [selectedGroup, setSelectedGroup] = React.useState<GroupedSelectOptions | undefined>(
            groupedMenuItems && groupedMenuItems.length > 0
                ? groupedMenuItems.find((group) => group.isSelected)
                : undefined,
        );

        const selectedItemIcon = React.useMemo(() => {
            const displayOption = menuItems?.find((m) => m.isSelected) || defaultOption;

            return displayOption?.startIcon || displayOption?.endIcon || null;
        }, [defaultOption, menuItems]);

        const { getPopperProps, getReferenceProps } = usePositioner({
            placement: 'bottom-start',
        });

        // Variables
        const SelectRef = React.useRef(null);
        const isGroupedItems = !!groupedMenuItems;
        const boundingBox = SelectRef && SelectRef.current ? SelectRef.current.getBoundingClientRect() : null;

        //this use effect is for the initial setup and updating it on external update
        useEffect(() => {
            if ((options.length <= 0 && groupOptions.length <= 0) || isExternalUpdated) {
                if (groupedMenuItems && !arraysEqual(groupOptions, groupedMenuItems)) {
                    setGroupOptions(groupedMenuItems);
                }

                const tempOptions = CalculateOptions(menuItems);
                if (!arraysEqual(options, tempOptions)) {
                    setOptions(tempOptions);
                }

                const newOptions = generateSelectedOptions();
                if (!arraysEqual(selectedOptions, newOptions)) {
                    setSelectedOptions(newOptions);
                }
            }
        }, [menuItems, groupedMenuItems]);

        const handleMultiSelectSelect = React.useCallback(
            (selectedOption: SelectOption) => {
                if (selectedOptionsIds.includes(String(selectedOption.id))) {
                    // only when not nested groups, in nested groups we navigate between groups and remember selections
                    setSelectedOptions((options) =>
                        options.filter((option) => option.id !== String(selectedOption.id)),
                    );
                } else {
                    setSelectedOptions((selected) => [
                        { id: String(selectedOption.id), value: selectedOption.value },
                        ...selected.filter((option) => option.id !== String(selectedOption.id)),
                    ]);
                }
            },
            [selectedOptionsIds],
        );

        const setOpenClose = React.useCallback(
            (isOpen: boolean) => {
                setIsVisible(isOpen);
                if (onOpenClose) {
                    onOpenClose(isOpen);
                }
            },
            [onOpenClose],
        );

        const handleSingleSelectSelect = React.useCallback(
            (selectedOption: SelectOption) => {
                setSelectedOptions([{ id: String(selectedOption.id), value: selectedOption.value }]);
                setOpenClose(false);
            },
            [setOpenClose],
        );

        const handleNestedGroupSelect = React.useCallback((selectedGroup: GroupedSelectOptions) => {
            if (selectedGroup.menuItems.length === 0) {
                // this is a reset (back) call
                setSelectedGroup(undefined);
            } else {
                setSelectedGroup(selectedGroup);
            }
        }, []);

        const handleOptionSelected = React.useCallback(
            (selectedOption: SelectOption | GroupedSelectOptions) => {
                if ('groupName' in selectedOption) {
                    handleNestedGroupSelect(selectedOption);
                } else {
                    isMultiselect ? handleMultiSelectSelect(selectedOption) : handleSingleSelectSelect(selectedOption);
                    if (!isDirty) {
                        setIsDirty(true);
                    }
                }

                // when using outer search, we will force focus back to search box after clicking item(s)
                if (useOuterSearch && inputRef && inputRef.current) {
                    inputRef.current.focus();
                }
            },
            [
                handleMultiSelectSelect,
                handleNestedGroupSelect,
                handleSingleSelectSelect,
                isDirty,
                isMultiselect,
                useOuterSearch,
            ],
        );

        /**
         * Update items and value every time the selected options changes
         */
        React.useEffect(() => {
            if (hasMounted) {
                if (isGroupedItems) {
                    setGroupOptions((groupOptions) => {
                        return groupOptions.map((group) => {
                            const newMenuItems = group.menuItems.map((option) => ({
                                ...option,
                                isSelected: selectedOptionsIds.includes(String(option.id)),
                            }));
                            return {
                                ...group,
                                menuItems: newMenuItems,
                            };
                        });
                    });
                } else {
                    setOptions((options) => {
                        return options.map((option) => ({
                            ...option,
                            isSelected: selectedOptionsIds.includes(String(option.id)),
                        }));
                    });
                }

                // update input values (only if not outer search)
                if (!useOuterSearch) {
                    if (maxDisplayValues && selectedOptions.length > maxDisplayValues) {
                        const count = selectedOptions.length;
                        setValue(count + ' ' + (maxSelectedMessage ?? 'Selected'));
                    } else {
                        setValue(selectedOptions.map((option) => option.value).join(', ') || '');
                    }
                }
            }
        }, [
            selectedOptions,
            maxDisplayValues,
            maxSelectedMessage,
            isGroupedItems,
            useOuterSearch,
            hasMounted,
            selectedOptionsIds,
        ]);

        // Callback when item is selected
        React.useEffect(() => {
            if (isDirty && hasMounted && onSelectItem && options && options.length > 0) {
                const items = options.filter((option) => option.isSelected);
                onSelectItem(items);
            }
        }, [options]);

        // Callback when group items are selected
        React.useEffect(() => {
            if (isDirty && hasMounted && onSelectItem && groupOptions && groupOptions.length > 0) {
                const items = groupOptions.map((group) => group.menuItems.filter((mi) => mi.isSelected)).flat();
                onSelectItem(items);
                if (resetGroupOnSelect) {
                    setSelectedGroup(undefined);
                }
            }
        }, [groupOptions]);

        // Callback when item is selected
        React.useEffect(() => {
            if (setVisible != null) {
                setIsVisible(setVisible);
            }
        }, [setVisible]);

        // store that we are mounted, to avoid useEffect triggers before mounting
        React.useEffect(() => setHasMounted(true), []);

        const handleClear = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
            e.stopPropagation();
            setIsDirty(true);
            setValue('');
            if (!remainOpenOnClear) {
                setOpenClose(false);
            }
            if (resetGroupOnClear) {
                setSelectedGroup(undefined);
            }

            if (deselectAllOnClear) {
                setSelectedOptions([]);
                setSelectedGroup(undefined);
            }
        };

        const handleCreate = (createdItem: SelectOption) => {
            // Create SelectOption
            onCreateItem && onCreateItem(createdItem);
            // Add to current options
            setOptions((options) => [createdItem, ...options]);
            // Select created SelectOption
            handleOptionSelected(createdItem);
            setOpenClose(false);
        };

        /**
         * User can override the default value to return a custom message
         */
        const getValueFormatted = inputValueFormat ? inputValueFormat() : value;

        const handleDropDownClick = () => {
            if (!isCreatable) {
                setOpenClose(!isVisible);
            } else {
                setOpenClose(true);
            }
        };

        const defaultStyle: React.CSSProperties = { borderStyle: isBorderless ? 'none' : 'solid', ...style };

        return (
            <div style={style} ref={ref}>
                <div data-testid={testId} ref={SelectRef} className={cx(styles.stackStyle)} {...passThroughProps}>
                    <FormControl {...passThroughProps} disabled={disabled} error={error} fullWidth>
                        <div
                            className={isInteractive ? cx(states) : ''}
                            style={{
                                cursor: 'pointer',
                                pointerEvents: 'all',
                            }}
                            onClick={handleDropDownClick}
                        >
                            <Input
                                {...getReferenceProps({ ref: inputRef })}
                                placeholder={placeholder ?? 'Select'}
                                autoComplete={autoComplete}
                                onInput={(inputValue) => setValue(inputValue.currentTarget.value)}
                                value={getValueFormatted}
                                data-testid={`input-${testId}`}
                                startAdornmentSlot={selectedItemIcon}
                                size={size}
                                endAdornmentSlot={
                                    <div className={cx(styles.endAdornmentSlot)}>
                                        {((selectedOptions.length > 0 && deselectAllOnClear) ||
                                            ((isCreatable || useOuterSearch) && String(value).length > 0)) && (
                                            <IconRemoveSmall
                                                data-testid={`clear-button-${testId}`}
                                                className={cx(styles.removeIcon)}
                                                style={{
                                                    cursor: 'pointer',
                                                    pointerEvents: 'all',
                                                    display: isClearButtonVisible ? 'block' : 'none',
                                                }}
                                                onClick={(e) => handleClear(e)}
                                            />
                                        )}
                                        {error && <IconAlertSmall className={cx(styles.errorIcon)} />}
                                        {isVisible ? <IconChevronUpSmall /> : <IconChevronDownSmall />}
                                    </div>
                                }
                                style={
                                    (!useOuterSearch || isMultiselect) && !disabled
                                        ? {
                                              ...defaultStyle,
                                              cursor: 'pointer',
                                              pointerEvents: 'all',
                                          }
                                        : { ...defaultStyle }
                                }
                                readOnly={!useOuterSearch}
                            />
                        </div>
                    </FormControl>
                </div>
                <SelectOptions
                    popperProps={getPopperProps}
                    groupedMenuItems={groupOptions}
                    parentRef={SelectRef}
                    setIsVisible={setIsVisible}
                    isVisible={isVisible}
                    menuItems={options}
                    onSelectItem={handleOptionSelected}
                    isSearchable={isSearchable}
                    isMultiselect={isMultiselect}
                    isCreatable={isCreatable}
                    parentBounds={boundingBox}
                    outerSearchValue={value}
                    onCreateItem={handleCreate}
                    useOuterSearch={useOuterSearch}
                    isNestedGroups={isNestedGroups}
                    selectedGroup={selectedGroup}
                    hideSelectedCheckmark={hideSelectedCheckmark}
                    withDebounce={withDebounce}
                    {...passThroughProps}
                />
            </div>
        );
    },
);

Select.displayName = 'Select';
